import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import {
  approveProductionDailyReport,
  confirmProductionDailyReport,
  createProductionDailyReport,
  deleteProductionDailyReport,
  deleteProductionDailyReportAttachment,
  downloadProductionDailyReportAttachment,
  fetchProductionDailyReport,
  fetchProductionDailyReports,
  reopenProductionDailyReport,
  revokeProductionDailyReport,
  updateProductionDailyReport,
  uploadProductionDailyReportAttachments,
  downloadProductionDailyReportPdf,
} from '../../api/productionDailyReport';
import { notifyError, reportError } from '../../utils/notify';
import { formatNumberInput, parseNumberInput } from '../../utils/numberFormat';
import { fetchCompanyInventory, fetchCompanyInventoryMasters } from '../../api/companyInventory';
import { fetchOrgMembers } from '../../api/org';
import ListPager from '../common/ListPager';
import { DEFAULT_PAGE_SIZE } from '../../types/page';
import { fetchProducts } from '../../api/product';
import { useCompanyStore } from '../../store/companyStore';
import {
  CompanyInventoryItem,
  CompanyInventoryMaster,
} from '../../types/companyInventory';
import { OrgMemberSummary } from '../../types/org';
import { Product } from '../../types/product';
import { canConfirmInspection } from '../../types/companyRole';
import { APPROVAL_TARGET_PRODUCTION_DAILY } from '../../types/approval';
import {
  ProductionDailyMaterial,
  ProductionDailyReport,
  ProductionDailyReportAttachment,
  ProductionDailyReportPayload,
  ProductionDailyRun,
} from '../../types/productionDailyReport';
import CompanyInventoryPickerModal from './CompanyInventoryPickerModal';
import ProductPickerModal from './ProductPickerModal';
import ApprovalCancelButton from '../approval/ApprovalCancelButton';
import ApprovalRequestModal from '../approval/ApprovalRequestModal';

import { withApprovalPendingClass } from '../../utils/approvalPending';
import ProductionDailyLotPicker, {
  ProductionDailyLotPick,
} from './ProductionDailyLotPicker';
import '../quality/QualityShell.css';
import '../quality/SmallGroupPanel.css';
import '../quality/EducationAnnualPlanTab.css';
import './InspectionFormSheet.css';
import './NonconformanceReportPanel.css';
import './ProductionDailyReportPanel.css';

type View =
  | { type: 'list' }
  | { type: 'create' }
  | { type: 'edit'; reportId: number }
  | { type: 'detail'; reportId: number };

const LIST_VIEW: View = { type: 'list' };

function uid(prefix = 'r') {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}

function emptyMaterial(): ProductionDailyMaterial {
  return {
    id: uid('m'),
    acceptanceInspectionId: null,
    inventoryId: null,
    itemName: '',
    specText: '',
    lotNo: '',
    qty: '',
    remark: '',
  };
}

function emptyRun(): ProductionDailyRun {
  return {
    id: uid('r'),
    productId: null,
    productCode: '',
    productName: '',
    spec: '',
    unit: '',
    workStart: '',
    workEnd: '',
    planQty: '',
    goodQty: '',
    defectQty: '',
    remark: '',
    materials: [],
  };
}

function qtyText(value: unknown): string {
  if (value == null || value === '') return '';
  const n = parseNumberInput(String(value));
  if (n == null) return String(value);
  return formatNumberInput(String(n));
}

function parseQty(value: string): number | null {
  return parseNumberInput(value);
}

/** HTML time input (HH:MM) 용 */
function toTimeInputValue(value?: string | null): string {
  if (!value?.trim()) return '';
  const m = value.trim().match(/^(\d{1,2}):(\d{2})(?::\d{2})?/);
  if (!m) return '';
  const h = Math.min(23, Math.max(0, Number(m[1])));
  const min = Math.min(59, Math.max(0, Number(m[2])));
  if (!Number.isFinite(h) || !Number.isFinite(min)) return '';
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

function parseId(raw: unknown): number | null {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string' && raw.trim()) {
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function isLegacyFlatItem(raw: Record<string, unknown>): boolean {
  const looksLikeProduct =
    'productId' in raw || 'goodQty' in raw || 'productName' in raw;
  const hasRunFields = Array.isArray(raw.materials) || 'workStart' in raw;
  return looksLikeProduct && !hasRunFields;
}

function parseMaterial(raw: Record<string, unknown>): ProductionDailyMaterial {
  return {
    id: String(raw.id || uid('m')),
    acceptanceInspectionId: parseId(raw.acceptanceInspectionId),
    inventoryId: parseId(raw.inventoryId),
    itemName: String(raw.itemName || ''),
    specText: String(raw.specText || ''),
    lotNo: String(raw.lotNo || ''),
    qty: qtyText(raw.qty),
    remark: String(raw.remark || ''),
  };
}

function parseRun(raw: Record<string, unknown>): ProductionDailyRun {
  const materialsRaw = Array.isArray(raw.materials) ? raw.materials : [];
  return {
    id: String(raw.id || uid('r')),
    productId: parseId(raw.productId),
    productCode: String(raw.productCode || ''),
    productName: String(raw.productName || ''),
    spec: String(raw.spec || ''),
    unit: String(raw.unit || ''),
    workStart: String(raw.workStart || ''),
    workEnd: String(raw.workEnd || ''),
    planQty: qtyText(raw.planQty),
    goodQty: qtyText(raw.goodQty),
    defectQty: qtyText(raw.defectQty),
    remark: String(raw.remark || ''),
    materials: materialsRaw.map((m) => parseMaterial((m || {}) as Record<string, unknown>)),
  };
}

function parseRuns(json?: string | null): ProductionDailyRun[] {
  if (!json?.trim()) return [];
  try {
    const arr = JSON.parse(json);
    if (!Array.isArray(arr) || !arr.length) return [];
    return arr.map((raw: Record<string, unknown>) => {
      if (isLegacyFlatItem(raw)) {
        const legacyLot = String(raw.lotNo || '').trim();
        const remark = String(raw.remark || '');
        return parseRun({
          ...raw,
          materials: [],
          workStart: '',
          workEnd: '',
          remark: legacyLot
            ? [remark, `구 LOT: ${legacyLot}`].filter(Boolean).join(' / ')
            : remark,
        });
      }
      return parseRun(raw);
    });
  } catch {
    return [];
  }
}

function runHasContent(run: ProductionDailyRun): boolean {
  return (
    run.productId != null ||
    !!run.workStart.trim() ||
    !!run.workEnd.trim() ||
    !!run.planQty.trim() ||
    !!run.goodQty.trim() ||
    !!run.defectQty.trim() ||
    !!run.remark.trim() ||
    run.materials.some((m) => m.inventoryId != null || m.qty.trim() || m.remark.trim())
  );
}

function serializeRuns(runs: ProductionDailyRun[]): string {
  const rows = runs.filter(runHasContent).map((run) => ({
    id: run.id,
    productId: run.productId,
    productCode: run.productCode.trim() || null,
    productName: run.productName.trim(),
    spec: run.spec.trim() || null,
    unit: run.unit.trim() || null,
    workStart: run.workStart.trim() || null,
    workEnd: run.workEnd.trim() || null,
    planQty: parseQty(run.planQty),
    goodQty: parseQty(run.goodQty),
    defectQty: parseQty(run.defectQty),
    remark: run.remark.trim() || null,
    materials: run.materials
      .filter((m) => m.inventoryId != null && parseQty(m.qty) != null)
      .map((m) => ({
        id: m.id,
        acceptanceInspectionId: m.acceptanceInspectionId,
        inventoryId: m.inventoryId,
        itemName: m.itemName.trim() || null,
        specText: m.specText.trim() || null,
        lotNo: m.lotNo.trim() || null,
        qty: parseQty(m.qty),
        remark: m.remark.trim() || null,
      })),
  }));
  return JSON.stringify(rows.length ? rows : []);
}

function formatSize(size?: number | null) {
  if (size == null || Number.isNaN(size) || size <= 0) return '';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function isApproved(status?: string | null) {
  return (status || '').toUpperCase() === 'APPROVED';
}

function StampBox({
  authorName,
  reviewerName,
  approverName,
}: {
  authorName?: string | null;
  reviewerName?: string | null;
  approverName?: string | null;
}) {
  return (
    <div className="ifs-sheet__stamps">
      {(
        [
          ['작성', authorName],
          ['검토', reviewerName],
          ['승인', approverName],
        ] as const
      ).map(([label, value]) => (
        <div key={label}>
          <strong>{label}</strong>
          <span style={{ color: value ? '#1f2a37' : '#98a2b3', fontWeight: value ? 600 : 400 }}>
            {value || '-'}
          </span>
        </div>
      ))}
    </div>
  );
}

function shiftLabel(shift?: string | null) {
  switch (shift) {
    case 'DAY':
      return '주간';
    case 'NIGHT':
      return '야간';
    case 'OTHER':
      return '기타';
    default:
      return shift || '-';
  }
}

function statusLabel(status?: string | null, approvalInProgress?: boolean | null) {
  if (approvalInProgress) return '결재 요청 중';
  if (status === 'APPROVED') return '승인';
  if (status === 'CONFIRMED') return '확정';
  return '작성중';
}

function memberLabel(m: OrgMemberSummary) {
  return m.displayName?.trim() || m.nickname?.trim() || `사용자 ${m.userId}`;
}

function runProductSummary(run: ProductionDailyRun) {
  if (!run.productName && run.productId == null) return '';
  const code = run.productCode?.trim();
  const name = run.productName?.trim() || (run.productId != null ? `제품 ${run.productId}` : '');
  return code ? `${code} · ${name}` : name;
}

function materialSummary(mat: ProductionDailyMaterial) {
  if (!mat.itemName && mat.inventoryId == null) return '';
  const name = mat.itemName?.trim() || (mat.inventoryId != null ? `품목 ${mat.inventoryId}` : '');
  const spec = mat.specText?.trim();
  return spec ? `${name} / ${spec}` : name;
}

const EMPTY_FORM: ProductionDailyReportPayload = {
  reportNo: '',
  reportDate: new Date().toISOString().slice(0, 10),
  shiftType: 'DAY',
  workCenter: '',
  supervisorUserId: null,
  supervisorName: '',
  workersText: '',
  equipmentText: '',
  workStart: '',
  workEnd: '',
  downtimeMinutes: undefined,
  downtimeReason: '',
  itemsJson: '',
  summaryText: '',
  issueText: '',
  status: 'DRAFT',
  remark: '',
};

function ProductionDailyReportPanel({
  contractId = null,
  embedded = false,
}: {
  contractId?: number | null;
  embedded?: boolean;
} = {}) {
  void embedded;
  const companyId = useCompanyStore((s) => s.companyId);
  const role = useCompanyStore((s) => s.role);
  const canApproveDirect = canConfirmInspection(role);
  const fileRef = useRef<HTMLInputElement>(null);

  const [view, setView] = useState<View>(LIST_VIEW);
  const [rows, setRows] = useState<ProductionDailyReport[]>([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [detail, setDetail] = useState<ProductionDailyReport | null>(null);
  const [keyword, setKeyword] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [approvalOpen, setApprovalOpen] = useState(false);
  const [approvalMessage, setApprovalMessage] = useState('');
  const [error, setError] = useState('');
  const [reportNoHint, setReportNoHint] = useState('');
  const [form, setForm] = useState<ProductionDailyReportPayload>(EMPTY_FORM);
  const [runs, setRuns] = useState<ProductionDailyRun[]>([]);
  const [downtimeMinutesText, setDowntimeMinutesText] = useState('');
  const [attachments, setAttachments] = useState<ProductionDailyReportAttachment[]>([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [orgMembers, setOrgMembers] = useState<OrgMemberSummary[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [inventory, setInventory] = useState<CompanyInventoryItem[]>([]);
  const [inventoryMasters, setInventoryMasters] = useState<CompanyInventoryMaster[]>([]);
  const [productPickerRunIdx, setProductPickerRunIdx] = useState<number | null>(null);
  const [inventoryPicker, setInventoryPicker] = useState<{
    runIdx: number;
    matIdx: number;
  } | null>(null);
  const [lotPicker, setLotPicker] = useState<{
    runIdx: number;
    matIdx: number;
    inventoryId: number;
  } | null>(null);

  const loadList = useCallback(
    async (q?: string, pageNum = 0) => {
      try {
        setLoading(true);
        const result = await fetchProductionDailyReports({
          keyword: q,
          contractId,
          page: pageNum,
          size: DEFAULT_PAGE_SIZE,
        });
        setRows(result.content || []);
        setPage(result.page ?? pageNum);
        setTotalPages(result.totalPages ?? 0);
        setTotalElements(result.totalElements ?? 0);
        setError('');
      } catch {
        reportError(setError, '생산일보 목록을 불러오지 못했습니다.');
        setRows([]);
        setTotalPages(0);
        setTotalElements(0);
      } finally {
        setLoading(false);
      }
    },
    [contractId]
  );

  const loadMasters = useCallback(async () => {
    try {
      const [members, productList, inventoryList, masterList] = await Promise.all([
        fetchOrgMembers(),
        fetchProducts(),
        fetchCompanyInventory(),
        fetchCompanyInventoryMasters(),
      ]);
      setOrgMembers(members);
      setProducts(productList.filter((p) => (p.useYn || 'Y') !== 'N'));
      setInventory(
        inventoryList.filter((row) => (row.itemStatus || 'ACTIVE') !== 'DISCONTINUED')
      );
      setInventoryMasters(masterList);
    } catch {
      setOrgMembers([]);
      setProducts([]);
      setInventory([]);
      setInventoryMasters([]);
    }
  }, []);

  const applyReportToForm = (row: ProductionDailyReport) => {
    setForm({
      reportNo: row.reportNo || '',
      reportDate: row.reportDate || new Date().toISOString().slice(0, 10),
      shiftType: row.shiftType || 'DAY',
      workCenter: row.workCenter || '',
      supervisorUserId: row.supervisorUserId ?? null,
      supervisorName: row.supervisorName || '',
      workersText: row.workersText || '',
      equipmentText: row.equipmentText || '',
      workStart: row.workStart || '',
      workEnd: row.workEnd || '',
      downtimeMinutes: row.downtimeMinutes ?? undefined,
      downtimeReason: row.downtimeReason || '',
      itemsJson: row.itemsJson || '',
      summaryText: row.summaryText || '',
      issueText: row.issueText || '',
      status: row.status || 'DRAFT',
      remark: row.remark || '',
    });
    setRuns(parseRuns(row.itemsJson));
    setDowntimeMinutesText(row.downtimeMinutes == null ? '' : String(row.downtimeMinutes));
    setAttachments(row.attachments || []);
    setPendingFiles([]);
  };

  useEffect(() => {
    setView(LIST_VIEW);
    setDetail(null);
    setKeyword('');
    setPage(0);
    void loadList('', 0);
    void loadMasters();
  }, [companyId, loadList, loadMasters, setView]);

  useEffect(() => {
    if (view.type !== 'list') return;
    const timer = window.setTimeout(() => {
      setPage(0);
      void loadList(keyword, 0);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [keyword, loadList, view.type]);

  useEffect(() => {
    if (view.type !== 'detail' && view.type !== 'edit') {
      if (view.type === 'list') setDetail(null);
      return;
    }
    let cancelled = false;
    void fetchProductionDailyReport(view.reportId)
      .then((data) => {
        if (cancelled) return;
        setDetail(data);
        if (view.type === 'edit') {
          if (isApproved(data.status) || !data.canEdit) {
            setView({ type: 'detail', reportId: data.reportId });
            if (isApproved(data.status)) {
              reportError(setError, '승인 완료된 생산일보는 수정할 수 없습니다.');
            } else {
              reportError(setError, '수정할 수 없는 생산일보입니다.');
            }
            return;
          }
          applyReportToForm(data);
          void loadMasters();
        }
        setError('');
      })
      .catch(() => {
        if (!cancelled) reportError(setError, '생산일보를 불러오지 못했습니다.');
      });
    return () => {
      cancelled = true;
    };
  }, [view, loadMasters]);

  const goList = () => {
    setView(LIST_VIEW);
    setDetail(null);
    void loadList(keyword, page);
  };

  const patch = <K extends keyof ProductionDailyReportPayload>(
    key: K,
    value: ProductionDailyReportPayload[K]
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const openCreate = async () => {
    setDetail(null);
    setForm({
      ...EMPTY_FORM,
      reportDate: new Date().toISOString().slice(0, 10),
      status: 'DRAFT',
    });
    setRuns([]);
    setDowntimeMinutesText('');
    setAttachments([]);
    setPendingFiles([]);
    setReportNoHint('저장 시 번호가 자동 발급됩니다.');
    setError('');
    setView({ type: 'create' });
    void loadMasters();
  };

  const openEdit = (reportId: number) => {
    if (detail && detail.reportId === reportId && isApproved(detail.status)) {
      reportError(setError, '승인 완료된 생산일보는 수정할 수 없습니다.');
      setView({ type: 'detail', reportId });
      return;
    }
    setReportNoHint('');
    setError('');
    setView({ type: 'edit', reportId });
  };

  const openDetail = (reportId: number) => {
    setError('');
    setView({ type: 'detail', reportId });
  };

  const buildPayload = (forceDraft: boolean): ProductionDailyReportPayload => {
    const trimmed = downtimeMinutesText.trim();
    const minutes =
      trimmed === '' ? undefined : Number.isFinite(Number(trimmed)) ? Number(trimmed) : undefined;
    return {
      ...form,
      reportNo: form.reportNo?.trim() || undefined,
      reportDate: form.reportDate,
      shiftType: form.shiftType || undefined,
      workCenter: form.workCenter?.trim() || undefined,
      supervisorUserId: form.supervisorUserId ?? null,
      workersText: form.workersText?.trim() || undefined,
      equipmentText: form.equipmentText?.trim() || undefined,
      workStart: form.workStart?.trim() || undefined,
      workEnd: form.workEnd?.trim() || undefined,
      downtimeMinutes: minutes,
      downtimeReason: form.downtimeReason?.trim() || undefined,
      itemsJson: serializeRuns(runs),
      summaryText: form.summaryText?.trim() || undefined,
      issueText: form.issueText?.trim() || undefined,
      status: forceDraft ? 'DRAFT' : form.status || 'DRAFT',
      remark: form.remark?.trim() || undefined,
      contractId: contractId ?? undefined,
    };
  };

  const validateForm = (): boolean => {
    if (!form.reportDate?.trim()) {
      reportError(setError, '일자를 입력해 주세요.');
      return false;
    }
    const filled = runs.filter(runHasContent);
    if (filled.length === 0) {
      reportError(setError, '생산 구간을 하나 이상 추가하고 제품을 선택해 주세요.');
      return false;
    }
    if (filled.some((run) => run.productId == null)) {
      reportError(setError, '생산 구간은 등록된 제품 중에서 선택해 주세요.');
      return false;
    }
    for (const run of filled) {
      for (const key of ['planQty', 'goodQty', 'defectQty'] as const) {
        if (run[key].trim() && parseQty(run[key]) == null) {
          reportError(setError, '계획/양품/불량 수량은 숫자로 입력해 주세요.');
          return false;
        }
      }
      for (const mat of run.materials) {
        if (mat.inventoryId == null && !mat.lotNo.trim() && !mat.qty.trim() && !mat.remark.trim()) {
          continue;
        }
        if (mat.inventoryId == null) {
          reportError(setError, '투입 품목을 선택해 주세요.');
          return false;
        }
        const qty = parseQty(mat.qty);
        if (qty == null || qty <= 0) {
          reportError(setError, '투입 수량을 0보다 큰 숫자로 입력해 주세요.');
          return false;
        }
      }
    }
    return true;
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSaving(true);
    setError('');
    try {
      const isCreate = view.type === 'create';
      const payload = buildPayload(isCreate);
      let saved: ProductionDailyReport;
      if (view.type === 'edit') {
        saved = await updateProductionDailyReport(view.reportId, payload);
      } else {
        saved = await createProductionDailyReport(payload);
      }
      const reportId = saved.reportId;
      let nextAttachments = saved.attachments || [];
      if (pendingFiles.length > 0 && reportId != null) {
        const uploaded = await uploadProductionDailyReportAttachments(reportId, pendingFiles);
        nextAttachments = [...nextAttachments, ...uploaded];
        setPendingFiles([]);
      }
      setAttachments(nextAttachments);
      setDetail({ ...saved, attachments: nextAttachments });
      if (isCreate) {
        window.alert(`문서번호 ${saved.reportNo || '-'}로 등록되었습니다.`);
      }
      setView({ type: 'detail', reportId });
      void loadList(keyword, page);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '생산일보 저장에 실패했습니다.';
      reportError(setError, message);
    } finally {
      setSaving(false);
    }
  };

  const isDraftForm =
    view.type === 'create' ||
    (view.type === 'edit' &&
      !isApproved(detail?.status || form.status) &&
      (detail?.status || form.status || 'DRAFT') !== 'CONFIRMED' &&
      !detail?.approvalInProgress);
  const detailAttachmentsMutable =
    view.type === 'detail' &&
    Boolean(detail?.canEdit) &&
    (detail?.status || '').toUpperCase() === 'DRAFT' &&
    !detail?.approvalInProgress;
  const attachmentsMutable = isDraftForm || detailAttachmentsMutable;

  const handleUploadNow = async (files: FileList | null) => {
    if (!files?.length || !attachmentsMutable) return;
    const selected = Array.from(files);
    if (fileRef.current) fileRef.current.value = '';
    if (view.type === 'create') {
      setPendingFiles((prev) => [...prev, ...selected]);
      return;
    }
    if (view.type !== 'edit' && view.type !== 'detail') return;
    setSaving(true);
    try {
      const uploaded = await uploadProductionDailyReportAttachments(view.reportId, selected);
      setAttachments((prev) => [...prev, ...uploaded]);
      setDetail((prev) =>
        prev
          ? {
              ...prev,
              attachments: [...(prev.attachments || []), ...uploaded],
              attachmentCount: (prev.attachmentCount ?? 0) + uploaded.length,
            }
          : prev
      );
      void loadList(keyword, page);
    } catch {
      reportError(setError, '첨부파일 업로드에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAttachment = async (attachment: ProductionDailyReportAttachment) => {
    if ((view.type !== 'edit' && view.type !== 'detail') || !attachmentsMutable) return;
    if (!window.confirm(`"${attachment.fileName}" 첨부를 삭제할까요?`)) return;
    setSaving(true);
    try {
      await deleteProductionDailyReportAttachment(view.reportId, attachment.attachmentId);
      setAttachments((prev) => prev.filter((a) => a.attachmentId !== attachment.attachmentId));
      setDetail((prev) =>
        prev
          ? {
              ...prev,
              attachments: (prev.attachments || []).filter(
                (a) => a.attachmentId !== attachment.attachmentId
              ),
              attachmentCount: Math.max(0, (prev.attachmentCount ?? 1) - 1),
            }
          : prev
      );
      void loadList(keyword, page);
    } catch {
      reportError(setError, '첨부파일 삭제에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (reportId: number) => {
    const target = detail?.reportId === reportId ? detail : rows.find((r) => r.reportId === reportId);
    if (isApproved(target?.status)) {
      reportError(setError, '승인 완료된 생산일보는 삭제할 수 없습니다.');
      return;
    }
    if (!window.confirm('이 생산일보를 삭제할까요?')) return;
    setSaving(true);
    try {
      await deleteProductionDailyReport(reportId);
      goList();
    } catch {
      reportError(setError, '생산일보 삭제에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirm = async () => {
    if (!detail) return;
    if ((detail.status || '').toUpperCase() !== 'APPROVED') {
      reportError(setError, '승인 완료된 생산일보만 확정할 수 있습니다.');
      return;
    }
    if (!window.confirm('이 생산일보를 확정할까요? 확정되면 회사 전원에게 공개됩니다.')) return;
    setSaving(true);
    try {
      const updated = await confirmProductionDailyReport(detail.reportId);
      setDetail(updated);
      void loadList(keyword, page);
      setError('');
    } catch {
      reportError(setError, '확정 처리에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleReopen = async () => {
    if (!detail) return;
    if ((detail.status || '').toUpperCase() !== 'CONFIRMED') {
      reportError(setError, '확정된 생산일보만 확정 취소할 수 있습니다.');
      return;
    }
    if (!window.confirm('확정을 취소할까요? 승인 상태로 돌아가며 다시 확정할 수 있습니다.')) return;
    setSaving(true);
    try {
      const updated = await reopenProductionDailyReport(detail.reportId);
      setDetail(updated);
      void loadList(keyword, page);
      setError('');
    } catch {
      reportError(setError, '확정 취소에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async () => {
    if (!detail) return;
    if (!canApproveDirect) {
      reportError(setError, '바로 승인은 오너/팀장만 가능합니다.');
      return;
    }
    if (
      !window.confirm(
        '생산일보를 바로 승인할까요? 승인 후에는 수정·삭제할 수 없습니다.'
      )
    ) {
      return;
    }
    try {
      setApproving(true);
      setError('');
      const saved = await approveProductionDailyReport(detail.reportId);
      setDetail(saved);
      setApprovalMessage('바로 승인되었습니다.');
      void loadList(keyword, page);
    } catch (err: unknown) {
      reportError(setError, (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          '바로 승인에 실패했습니다.');
    } finally {
      setApproving(false);
    }
  };

  const handleRevoke = async () => {
    if (!detail) return;
    if (!window.confirm('승인을 철회할까요?\n승인란·검토란이 지워집니다.')) return;
    try {
      setApproving(true);
      setError('');
      const saved = await revokeProductionDailyReport(detail.reportId);
      setDetail(saved);
      setApprovalMessage('승인이 철회되었습니다.');
      void loadList(keyword, page);
    } catch (err: unknown) {
      reportError(setError, (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          '승인 철회에 실패했습니다.');
    } finally {
      setApproving(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!detail) return;
    try {
      setPdfDownloading(true);
      const name = detail.reportNo?.trim() || detail.reportDate || String(detail.reportId);
      await downloadProductionDailyReportPdf(detail.reportId, `생산일보_${name}.pdf`);
    } catch (err) {
      notifyError(
        err instanceof Error
          ? err.message
          : 'PDF 다운로드에 실패했습니다. Gotenberg 실행 여부를 확인해 주세요.'
      );
    } finally {
      setPdfDownloading(false);
    }
  };

  const renderEditActions = () => (
    <div className="quality-form__actions">
      <button type="button" className="ncr-btn is-secondary" onClick={goList}>
        취소
      </button>
      <button type="submit" className="ncr-btn is-primary" disabled={saving}>
        {saving ? '저장 중...' : '저장'}
      </button>
    </div>
  );

  const renderDetailActions = () => (
    <div className="ncr-actions">
      {isApproved(detail?.status) || (detail?.status || '').toUpperCase() === 'CONFIRMED' ? (
        <button
          type="button"
          className="ncr-btn is-primary"
          disabled={pdfDownloading}
          onClick={() => void handleDownloadPdf()}
        >
          {pdfDownloading ? 'PDF 생성 중…' : 'PDF 다운로드'}
        </button>
      ) : null}
      {detail?.canRevoke ? (
        <button
          type="button"
          className="ncr-btn is-danger"
          disabled={approving}
          onClick={() => void handleRevoke()}
        >
          {approving ? '철회 중...' : '승인 철회'}
        </button>
      ) : null}
      {detail?.canCancelApproval ? (
        <ApprovalCancelButton
          detail={detail}
          className="ncr-btn is-secondary"
          disabled={approving}
          onCancelled={async () => {
            setDetail(await fetchProductionDailyReport(detail.reportId));
            setApprovalMessage('');
            void loadList(keyword, page);
          }}
        />
      ) : null}
      {(detail?.status || '').toUpperCase() === 'DRAFT' && !detail?.approvalInProgress ? (
        <>
          {detail?.canEdit ? (
            <button
              type="button"
              className="ncr-btn is-secondary"
              onClick={() => openEdit(detail.reportId)}
            >
              수정
            </button>
          ) : null}
          <button
            type="button"
            className="ncr-btn is-primary"
            disabled={approving}
            onClick={() => {
              setApprovalMessage('');
              setApprovalOpen(true);
            }}
          >
            결재 요청
          </button>
          {canApproveDirect ? (
            <button
              type="button"
              className="ncr-btn is-approve"
              disabled={approving}
              onClick={() => void handleApprove()}
            >
              {approving ? '승인 중...' : '승인'}
            </button>
          ) : null}
          {detail?.canDelete ? (
            <button
              type="button"
              className="ncr-btn is-danger"
              disabled={saving || approving}
              onClick={() => void handleDelete(detail.reportId)}
            >
              삭제
            </button>
          ) : null}
        </>
      ) : null}
      {isApproved(detail?.status) ? (
        detail?.canConfirm ? (
          <button
            type="button"
            className="ncr-btn is-primary"
            disabled={saving}
            onClick={() => void handleConfirm()}
          >
            확정
          </button>
        ) : null
      ) : null}
      {(detail?.status || '').toUpperCase() === 'CONFIRMED' && detail?.canReopen ? (
        <button
          type="button"
          className="ncr-btn is-secondary"
          disabled={saving}
          onClick={() => void handleReopen()}
        >
          확정 취소
        </button>
      ) : null}
      <button type="button" className="ncr-btn is-secondary" onClick={goList}>
        목록으로
      </button>
    </div>
  );

  const updateRun = (idx: number, patchRun: Partial<ProductionDailyRun>) => {
    setRuns((prev) => prev.map((row, i) => (i === idx ? { ...row, ...patchRun } : row)));
  };

  const applyProduct = (idx: number, product: Product) => {
    updateRun(idx, {
      productId: product.productId,
      productCode: product.productCode || '',
      productName: product.productName || '',
      spec: product.spec || '',
      unit: product.unit || '',
    });
  };

  const clearProduct = (idx: number) => {
    updateRun(idx, {
      productId: null,
      productCode: '',
      productName: '',
      spec: '',
      unit: '',
    });
  };

  const updateMaterial = (
    runIdx: number,
    matIdx: number,
    patchMat: Partial<ProductionDailyMaterial>
  ) => {
    setRuns((prev) =>
      prev.map((run, i) => {
        if (i !== runIdx) return run;
        return {
          ...run,
          materials: run.materials.map((m, j) => (j === matIdx ? { ...m, ...patchMat } : m)),
        };
      })
    );
  };

  const applyInventoryItem = (runIdx: number, matIdx: number, item: CompanyInventoryItem) => {
    updateMaterial(runIdx, matIdx, {
      acceptanceInspectionId: null,
      inventoryId: item.inventoryId,
      itemName: item.itemName || '',
      specText: item.specText || '',
      lotNo: '',
    });
  };

  const applyLotPick = (pick: ProductionDailyLotPick) => {
    if (!lotPicker) return;
    const { runIdx, matIdx } = lotPicker;
    const current = runs[runIdx]?.materials[matIdx];
    updateMaterial(runIdx, matIdx, {
      acceptanceInspectionId: pick.acceptanceInspectionId,
      inventoryId: pick.inventoryId,
      itemName: pick.itemName || current?.itemName || '',
      specText: pick.specText || current?.specText || '',
      lotNo: pick.lotNo,
      qty: current?.qty?.trim() ? current.qty : pick.remainingQty || pick.incomingQty || current?.qty || '',
    });
    setLotPicker(null);
  };

  const openLotPicker = (runIdx: number, matIdx: number, inventoryId: number | null) => {
    if (inventoryId == null) {
      notifyError('먼저 투입 품목을 선택해 주세요.');
      return;
    }
    setLotPicker({ runIdx, matIdx, inventoryId });
  };

  const detailRuns =
    detail?.itemsJson != null
      ? parseRuns(detail.itemsJson).filter((run) => run.productId != null || run.productName.trim())
      : [];

  const renderForm = () => (
    <form className="quality-form is-dense" onSubmit={save}>
      {renderEditActions()}
      <section className="quality-form__section">
        <h3>{view.type === 'create' ? '생산일보 작성' : '생산일보 수정'}</h3>
        <div className="quality-form__grid is-dense">
          <label>
            번호
            <input
              value={view.type === 'create' ? '' : form.reportNo || ''}
              readOnly
              placeholder={view.type === 'create' ? '저장 시 자동 발급' : undefined}
              maxLength={100}
            />
            {view.type === 'create' && reportNoHint ? (
              <span className="quality-form__hint">{reportNoHint}</span>
            ) : null}
          </label>
          <label>
            일자 *
            <input
              type="date"
              value={form.reportDate || ''}
              onChange={(e) => patch('reportDate', e.target.value)}
              required
            />
          </label>
          <label>
            교대
            <select
              value={form.shiftType || 'DAY'}
              onChange={(e) => patch('shiftType', e.target.value)}
            >
              <option value="DAY">주간</option>
              <option value="NIGHT">야간</option>
              <option value="OTHER">기타</option>
            </select>
          </label>
          <label>
            라인/작업장
            <input
              value={form.workCenter || ''}
              onChange={(e) => patch('workCenter', e.target.value)}
            />
          </label>
          <label>
            책임자
            <select
              value={form.supervisorUserId ?? ''}
              onChange={(e) =>
                patch('supervisorUserId', e.target.value ? Number(e.target.value) : null)
              }
            >
              <option value="">선택</option>
              {orgMembers.map((m) => (
                <option key={m.userId} value={m.userId}>
                  {memberLabel(m)}
                  {m.departmentName ? ` (${m.departmentName})` : ''}
                </option>
              ))}
            </select>
          </label>
          <label>
            비가동(분) / 사유
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                type="number"
                min={0}
                value={downtimeMinutesText}
                onChange={(e) => setDowntimeMinutesText(e.target.value)}
                placeholder="분"
                style={{ width: 72, flex: '0 0 72px' }}
              />
              <input
                value={form.downtimeReason || ''}
                onChange={(e) => patch('downtimeReason', e.target.value)}
                placeholder="사유"
                style={{ flex: 1 }}
              />
            </div>
          </label>
          <label className="is-span-2">
            작업인원
            <textarea
              rows={2}
              value={form.workersText || ''}
              onChange={(e) => patch('workersText', e.target.value)}
              placeholder="인원·조 구성 등"
            />
          </label>
          <label>
            설비
            <textarea
              rows={2}
              value={form.equipmentText || ''}
              onChange={(e) => patch('equipmentText', e.target.value)}
            />
          </label>
          <label className="is-span-2">
            작업 요약
            <textarea
              rows={2}
              value={form.summaryText || ''}
              onChange={(e) => patch('summaryText', e.target.value)}
            />
          </label>
          <label>
            이슈/특이사항
            <textarea
              rows={2}
              value={form.issueText || ''}
              onChange={(e) => patch('issueText', e.target.value)}
            />
          </label>
          <label className="is-span-2">
            비고
            <textarea
              rows={2}
              value={form.remark || ''}
              onChange={(e) => patch('remark', e.target.value)}
            />
          </label>
        </div>
      </section>

      <section className="quality-form__section">
        <div className="pdr-section-head">
          <h3>생산 구간</h3>
          <button
            type="button"
            className="ncr-btn is-secondary"
            onClick={() => setRuns((prev) => [...prev, emptyRun()])}
          >
            구간 추가
          </button>
        </div>
        <p className="quality-form__hint">
          투입은 기록용입니다. 회사 재고에서 품목을 고른 뒤 Lot No.는 「찾기」로 고르거나 직접
          입력하세요. 재고 차감은 되지 않습니다. (실차감은 중간검사)
        </p>
        {products.length === 0 ? (
          <p className="quality-form__hint">
            선택 가능한 제품이 없습니다. 제품 메뉴에서 먼저 등록해 주세요.
          </p>
        ) : null}
        {runs.length === 0 ? (
          <p className="quality-form__hint">구간 추가 버튼으로 생산 구간을 추가하세요.</p>
        ) : null}
        {runs.map((run, idx) => (
          <div key={run.id} className="pdr-run-card">
            <div className="pdr-run-card__top">
              <label className="pdr-run-card__product">
                제품 *
                <div className="pdr-pick-row">
                  <button
                    type="button"
                    className={`pdr-pick-btn${runProductSummary(run) ? ' has-value' : ''}`}
                    onClick={() => setProductPickerRunIdx(idx)}
                  >
                    {runProductSummary(run) || '제품 선택'}
                  </button>
                  {run.productId != null ? (
                    <button
                      type="button"
                      className="ncr-btn is-ghost pdr-pick-clear"
                      onClick={() => clearProduct(idx)}
                    >
                      지우기
                    </button>
                  ) : null}
                </div>
              </label>
              <button
                type="button"
                className="ncr-btn is-danger pdr-run-card__remove"
                onClick={() => setRuns((prev) => prev.filter((_, i) => i !== idx))}
              >
                구간 삭제
              </button>
            </div>
            {(run.spec || run.unit) && (
              <p className="pdr-run-card__meta">
                {[run.spec, run.unit].filter(Boolean).join(' · ')}
              </p>
            )}
            <div className="pdr-run-card__metrics">
              <label>
                시작
                <input
                  type="time"
                  value={toTimeInputValue(run.workStart)}
                  onChange={(e) => updateRun(idx, { workStart: e.target.value })}
                />
              </label>
              <label>
                종료
                <input
                  type="time"
                  value={toTimeInputValue(run.workEnd)}
                  onChange={(e) => updateRun(idx, { workEnd: e.target.value })}
                />
              </label>
              <label>
                계획
                <input
                  type="text"
                  inputMode="decimal"
                  value={run.planQty}
                  onChange={(e) =>
                    updateRun(idx, { planQty: formatNumberInput(e.target.value) })
                  }
                />
              </label>
              <label>
                양품
                <input
                  type="text"
                  inputMode="decimal"
                  value={run.goodQty}
                  onChange={(e) =>
                    updateRun(idx, { goodQty: formatNumberInput(e.target.value) })
                  }
                />
              </label>
              <label>
                불량
                <input
                  type="text"
                  inputMode="decimal"
                  value={run.defectQty}
                  onChange={(e) =>
                    updateRun(idx, { defectQty: formatNumberInput(e.target.value) })
                  }
                />
              </label>
            </div>

            <div className="pdr-run-card__materials">
              <div className="pdr-run-card__materials-head">
                <h4>투입 자재</h4>
                <button
                  type="button"
                  className="ncr-btn is-secondary"
                  onClick={() =>
                    updateRun(idx, { materials: [...run.materials, emptyMaterial()] })
                  }
                >
                  투입 추가
                </button>
              </div>
              {run.materials.length === 0 ? (
                <p className="quality-form__hint">투입 자재가 없습니다.</p>
              ) : (
                <div className="pdr-mat-scroll">
                  <table className="pdr-mat-table">
                    <thead>
                      <tr>
                        <th>품목</th>
                        <th className="is-lot">Lot No.</th>
                        <th className="is-qty">수량</th>
                        <th className="is-remark">비고</th>
                        <th className="is-actions" />
                      </tr>
                    </thead>
                    <tbody>
                      {run.materials.map((mat, matIdx) => {
                        const summary = materialSummary(mat);
                        return (
                          <tr key={mat.id}>
                            <td>
                              <button
                                type="button"
                                className={`pdr-pick-btn${summary ? ' has-value' : ''}`}
                                onClick={() => setInventoryPicker({ runIdx: idx, matIdx })}
                              >
                                {summary || '품목 선택'}
                              </button>
                            </td>
                            <td className="is-lot">
                              <div className="pdr-mat-lot">
                                <input
                                  value={mat.lotNo}
                                  onChange={(e) =>
                                    updateMaterial(idx, matIdx, {
                                      lotNo: e.target.value,
                                      acceptanceInspectionId: null,
                                    })
                                  }
                                  placeholder="직접 입력"
                                  maxLength={100}
                                />
                                <button
                                  type="button"
                                  className="pdr-mat-lot__btn"
                                  disabled={mat.inventoryId == null}
                                  title={
                                    mat.inventoryId == null
                                      ? '품목을 먼저 선택하세요'
                                      : '이 품목의 로트 목록에서 선택'
                                  }
                                  onClick={() => openLotPicker(idx, matIdx, mat.inventoryId)}
                                >
                                  찾기
                                </button>
                              </div>
                            </td>
                            <td className="is-qty">
                              <input
                                type="text"
                                inputMode="decimal"
                                value={mat.qty}
                                onChange={(e) =>
                                  updateMaterial(idx, matIdx, {
                                    qty: formatNumberInput(e.target.value),
                                  })
                                }
                              />
                            </td>
                            <td className="is-remark">
                              <input
                                value={mat.remark}
                                onChange={(e) =>
                                  updateMaterial(idx, matIdx, { remark: e.target.value })
                                }
                              />
                            </td>
                            <td className="is-actions">
                              <button
                                type="button"
                                className="ncr-btn is-danger"
                                onClick={() =>
                                  updateRun(idx, {
                                    materials: run.materials.filter((_, j) => j !== matIdx),
                                  })
                                }
                              >
                                삭제
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        ))}
      </section>

      <section className="quality-form__section">
        <div className="pdr-section-head">
          <h3>첨부파일</h3>
          {isDraftForm ? (
            <button
              type="button"
              className="ncr-btn is-secondary"
              disabled={saving}
              onClick={() => fileRef.current?.click()}
            >
              파일 추가
            </button>
          ) : null}
          <input
            ref={fileRef}
            type="file"
            multiple
            hidden
            onChange={(e) => void handleUploadNow(e.target.files)}
          />
        </div>
        <p className="edu-plan-hint">
          작업일지·사진 등을 첨부할 수 있습니다.
          {view.type === 'create' ? ' 신규 작성 시 저장과 함께 업로드됩니다.' : ''}
          {!isDraftForm ? ' 확정된 일보는 첨부를 변경할 수 없습니다.' : ''}
        </p>
        <ul className="edu-plan-files">
          {attachments.map((file) => (
            <li key={file.attachmentId}>
              <div>
                <strong>{file.fileName}</strong>
                <span>{formatSize(file.fileSize)}</span>
              </div>
              <div className="edu-plan-files__actions">
                {view.type === 'edit' ? (
                  <button
                    type="button"
                    className="ncr-btn is-secondary"
                    onClick={() =>
                      void downloadProductionDailyReportAttachment(
                        view.reportId,
                        file.attachmentId,
                        file.fileName
                      ).catch(() => reportError(setError, '첨부파일 다운로드에 실패했습니다.'))
                    }
                  >
                    다운로드
                  </button>
                ) : null}
                {isDraftForm && view.type === 'edit' ? (
                  <button
                    type="button"
                    className="ncr-btn is-danger"
                    disabled={saving}
                    onClick={() => void handleDeleteAttachment(file)}
                  >
                    삭제
                  </button>
                ) : null}
              </div>
            </li>
          ))}
          {pendingFiles.map((file, idx) => (
            <li key={`pending-${file.name}-${idx}`}>
              <div>
                <strong>{file.name}</strong>
                <span>저장 대기 · {formatSize(file.size)}</span>
              </div>
              <button
                type="button"
                className="ncr-btn is-danger"
                onClick={() => setPendingFiles((prev) => prev.filter((_, i) => i !== idx))}
              >
                제거
              </button>
            </li>
          ))}
          {attachments.length === 0 && pendingFiles.length === 0 && (
            <li className="is-empty">등록된 첨부파일이 없습니다.</li>
          )}
        </ul>
      </section>

      {renderEditActions()}
    </form>
  );

  return (
    <main
      className={`quality-shell pdr-panel${view.type === 'list' ? ' is-list-view' : ' sg-panel'}`}
    >
      {view.type === 'list' ? (
        <div className="quality-shell__toolbar pdr-list-header">
          <div>
            <h2>{contractId != null ? '계약 생산일보' : '생산일보'}</h2>
            <p>작성 → 결재/승인 → 확정 순으로 진행합니다. 확정되면 회사 전원에게 공개됩니다.</p>
          </div>
          <button type="button" className="ncr-btn is-primary" onClick={() => void openCreate()}>
            일보 등록
          </button>
        </div>
      ) : null}

      {error && <p className="quality-error">{error}</p>}

      {view.type === 'list' ? (
        <>
          <div className="quality-shell__toolbar pdr-list-search">
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="번호 / 라인 / 책임자 / 작성자 검색"
            />
          </div>
          <div className="paged-list">
            <div className="paged-list__scroll">
              <table className="quality-table">
                <thead>
                  <tr>
                    <th>번호</th>
                    <th>일자</th>
                    <th>교대</th>
                    <th>라인/작업장</th>
                    <th>책임자</th>
                    <th>작성자</th>
                    <th>첨부</th>
                    <th>상태</th>
                    <th>관리</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={9}>불러오는 중…</td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={9}>등록된 생산일보가 없습니다.</td>
                    </tr>
                  ) : (
                    rows.map((row) => (
                      <tr
                        key={row.reportId}
                        onClick={() => openDetail(row.reportId)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td>{row.reportNo || '-'}</td>
                        <td>{row.reportDate || '-'}</td>
                        <td>{shiftLabel(row.shiftType)}</td>
                        <td>{row.workCenter || '-'}</td>
                        <td>{row.supervisorName || '-'}</td>
                        <td>{row.authorName || '-'}</td>
                        <td>{row.attachmentCount ?? 0}</td>
                        <td>
                          <span
                            className={`quality-badge is-${withApprovalPendingClass(
                              (row.status || 'draft').toLowerCase(),
                              row.approvalInProgress
                            )}`}
                          >
                            {statusLabel(row.status, row.approvalInProgress)}
                          </span>
                        </td>
                        <td>
                          {row.canDelete && !isApproved(row.status) && !row.approvalInProgress ? (
                            <button
                              type="button"
                              className="ncr-btn is-danger"
                              disabled={saving}
                              onClick={(e) => {
                                e.stopPropagation();
                                void handleDelete(row.reportId);
                              }}
                            >
                              삭제
                            </button>
                          ) : (
                            '-'
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <ListPager
              page={page}
              totalPages={totalPages}
              totalElements={totalElements}
              loading={loading}
              onChange={(next) => {
                setPage(next);
                void loadList(keyword, next);
              }}
            />
          </div>
        </>
      ) : view.type === 'create' || view.type === 'edit' ? (
        <>
          <div className="quality-shell__toolbar pdr-toolbar">
            <div>
              <h2>{view.type === 'edit' ? '생산일보 수정' : '생산일보 작성'}</h2>
              <p>작성 → 결재/승인 → 확정 순으로 진행합니다. 확정되면 회사 전원에게 공개됩니다.</p>
            </div>
            <button type="button" className="ncr-btn is-secondary" onClick={goList}>
              목록으로
            </button>
          </div>
          {renderForm()}
        </>
      ) : (
        <>
          <div className="quality-shell__toolbar pdr-toolbar">
            <div>
              <h2>생산일보 상세</h2>
              <p>
                {detail?.reportNo || '-'} ·{' '}
                <span
                  className={`quality-badge is-${withApprovalPendingClass(
                    (detail?.status || 'draft').toLowerCase(),
                    detail?.approvalInProgress
                  )}`}
                >
                  {statusLabel(detail?.status, detail?.approvalInProgress)}
                </span>
              </p>
              {detail?.approvalInProgress ? (
                <p className="quality-form__hint" style={{ margin: '4px 0 0' }}>
                  결재가 진행 중입니다. 내용을 바꾸려면 결재 요청을 취소한 뒤 수정하고 다시 요청하세요.
                </p>
              ) : null}
            </div>
            {renderDetailActions()}
          </div>

          {approvalMessage ? (
            <p className="quality-form__hint" style={{ color: '#1a5fb4' }}>
              {approvalMessage}
            </p>
          ) : null}
          {detail?.approvalInProgress ? (
            <p className="quality-form__hint">
              결재가 진행 중입니다. 내용을 바꾸려면 결재 요청을 취소한 뒤 수정하고 다시 요청하세요.
            </p>
          ) : null}
          {!detail ? (
            <p className="quality-form__hint">불러오는 중…</p>
          ) : (
            <div className="ncr-detail">
              <div
                className="ifs-sheet"
                style={{ marginBottom: 14, padding: '12px 14px', background: '#fff' }}
              >
                <div className="ifs-sheet__top" style={{ marginBottom: 0 }}>
                  <div>
                    <h4 className="ifs-sheet__title" style={{ margin: 0, fontSize: 15 }}>
                      생산일보 {detail.reportNo || ''}
                    </h4>
                    <p className="ifs-sheet__subtitle" style={{ margin: '4px 0 0' }}>
                      {detail.reportDate || '-'} · {statusLabel(detail.status, detail.approvalInProgress)}
                    </p>
                  </div>
                  <StampBox
                    authorName={detail.authorName}
                    reviewerName={detail.reviewerName}
                    approverName={detail.approverName}
                  />
                </div>
              </div>
              <section className="ncr-detail__card">
                <h3>기본 정보</h3>
                <dl>
                  <dt>번호</dt>
                  <dd>{detail.reportNo || '-'}</dd>
                  <dt>일자</dt>
                  <dd>{detail.reportDate || '-'}</dd>
                  <dt>교대</dt>
                  <dd>{shiftLabel(detail.shiftType)}</dd>
                  <dt>상태</dt>
                  <dd>
                    <span
                      className={`quality-badge is-${(detail.status || 'draft').toLowerCase()}`}
                    >
                      {statusLabel(detail.status, detail.approvalInProgress)}
                    </span>
                  </dd>
                  <dt>라인/작업장</dt>
                  <dd>{detail.workCenter || '-'}</dd>
                  <dt>책임자</dt>
                  <dd>{detail.supervisorName || '-'}</dd>
                  <dt>작성자</dt>
                  <dd>{detail.authorName || '-'}</dd>
                </dl>
              </section>

              <section className="ncr-detail__card">
                <h3>비가동</h3>
                <dl>
                  <dt>비가동(분)</dt>
                  <dd>{detail.downtimeMinutes ?? '-'}</dd>
                  <dt>비가동 사유</dt>
                  <dd>{detail.downtimeReason || '-'}</dd>
                </dl>
              </section>

              <section className="ncr-detail__card is-wide">
                <h3>인원·설비</h3>
                <dl>
                  <dt>작업인원</dt>
                  <dd>{detail.workersText || '-'}</dd>
                  <dt>설비</dt>
                  <dd>{detail.equipmentText || '-'}</dd>
                </dl>
              </section>

              <section className="ncr-detail__card is-wide">
                <h3>생산 구간</h3>
                {detailRuns.length === 0 ? (
                  <p className="quality-form__hint">등록된 생산 구간이 없습니다.</p>
                ) : (
                  detailRuns.map((run) => {
                    const timeRange =
                      run.workStart || run.workEnd
                        ? `${run.workStart || '?'} – ${run.workEnd || '?'}`
                        : '시간 미입력';
                    const product =
                      run.productCode
                        ? `${run.productCode} · ${run.productName || '-'}`
                        : run.productName || '-';
                    return (
                      <div key={run.id} className="pdr-detail-run">
                        <p className="pdr-detail-run__title">
                          {timeRange} · {product}
                        </p>
                        <p className="pdr-detail-run__qtys">
                          계획 {run.planQty || '-'} · 양품 {run.goodQty || '-'} · 불량{' '}
                          {run.defectQty || '-'}
                          {run.unit ? ` (${run.unit})` : ''}
                          {run.spec ? ` · ${run.spec}` : ''}
                        </p>
                        {run.materials.length > 0 ? (
                          <ul className="pdr-detail-run__materials">
                            {run.materials.map((m) => (
                              <li key={m.id}>
                                {[m.lotNo ? `Lot ${m.lotNo}` : null, m.itemName || null]
                                  .filter(Boolean)
                                  .join(' · ') || '투입'}
                                {m.qty ? ` · ${m.qty}` : ''}
                                {m.remark ? ` (${m.remark})` : ''}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="quality-form__hint">투입 자재 없음</p>
                        )}
                      </div>
                    );
                  })
                )}
              </section>

              <section className="ncr-detail__card is-wide">
                <h3>내용</h3>
                <dl>
                  <dt>작업 요약</dt>
                  <dd>{detail.summaryText || '-'}</dd>
                  <dt>이슈/특이사항</dt>
                  <dd>{detail.issueText || '-'}</dd>
                  <dt>비고</dt>
                  <dd>{detail.remark || '-'}</dd>
                </dl>
              </section>

              <section className="ncr-detail__card is-wide">
                <div className="quality-shell__toolbar" style={{ marginBottom: 12 }}>
                  <h3 style={{ margin: 0 }}>첨부파일</h3>
                  {detailAttachmentsMutable ? (
                    <>
                      <button
                        type="button"
                        className="ncr-btn is-secondary"
                        disabled={saving}
                        onClick={() => fileRef.current?.click()}
                      >
                        파일 추가
                      </button>
                      <input
                        ref={fileRef}
                        type="file"
                        multiple
                        hidden
                        onChange={(e) => void handleUploadNow(e.target.files)}
                      />
                    </>
                  ) : null}
                </div>
                <ul className="edu-plan-files">
                  {(detail.attachments || []).length === 0 ? (
                    <li className="is-empty">등록된 첨부파일이 없습니다.</li>
                  ) : (
                    (detail.attachments || []).map((file) => (
                      <li key={file.attachmentId}>
                        <div>
                          <strong>{file.fileName}</strong>
                          <span>{formatSize(file.fileSize)}</span>
                        </div>
                        <div className="edu-plan-files__actions">
                          <button
                            type="button"
                            className="ncr-btn is-secondary"
                            onClick={() =>
                              void downloadProductionDailyReportAttachment(
                                detail.reportId,
                                file.attachmentId,
                                file.fileName
                              ).catch(() => reportError(setError, '첨부파일 다운로드에 실패했습니다.'))
                            }
                          >
                            다운로드
                          </button>
                          {detailAttachmentsMutable ? (
                            <button
                              type="button"
                              className="ncr-btn is-danger"
                              disabled={saving}
                              onClick={() => void handleDeleteAttachment(file)}
                            >
                              삭제
                            </button>
                          ) : null}
                        </div>
                      </li>
                    ))
                  )}
                </ul>
              </section>
            </div>
          )}
          {renderDetailActions()}
          {detail ? (
            <ApprovalRequestModal
              open={approvalOpen}
              titleDefault={`생산일보 ${detail.reportNo || ''} 결재`.trim()}
              targetType={APPROVAL_TARGET_PRODUCTION_DAILY}
              targetId={detail.reportId}
              onClose={() => setApprovalOpen(false)}
              onSubmitted={() => {
                setApprovalOpen(false);
                setApprovalMessage(
                  '결재 요청이 등록되었습니다. 헤더의 결재 탭에서 확인할 수 있습니다.'
                );
                void fetchProductionDailyReport(detail.reportId).then(setDetail);
                void loadList(keyword, page);
              }}
            />
          ) : null}
        </>
      )}
      {lotPicker ? (
        <ProductionDailyLotPicker
          inventory={
            inventory.find((row) => row.inventoryId === lotPicker.inventoryId) ||
            ({
              inventoryId: lotPicker.inventoryId,
              itemKind: 'MATERIAL',
              itemName:
                runs[lotPicker.runIdx]?.materials[lotPicker.matIdx]?.itemName || '품목',
              specText:
                runs[lotPicker.runIdx]?.materials[lotPicker.matIdx]?.specText || '',
              stockQty: 0,
            } as CompanyInventoryItem)
          }
          onClose={() => setLotPicker(null)}
          onSelect={applyLotPick}
        />
      ) : null}
      <ProductPickerModal
        open={productPickerRunIdx != null}
        products={products}
        title="생산 제품 선택"
        onClose={() => setProductPickerRunIdx(null)}
        onListsUpdated={setProducts}
        onSelect={(product) => {
          if (productPickerRunIdx == null) return;
          applyProduct(productPickerRunIdx, product);
        }}
      />
      <CompanyInventoryPickerModal
        open={inventoryPicker != null}
        items={inventory}
        masters={inventoryMasters}
        title="투입 자재 선택"
        onClose={() => setInventoryPicker(null)}
        onListsUpdated={(nextInventory, nextMasters) => {
          setInventory(
            nextInventory.filter((row) => (row.itemStatus || 'ACTIVE') !== 'DISCONTINUED')
          );
          setInventoryMasters(nextMasters);
        }}
        onSelect={(item) => {
          if (inventoryPicker == null) return;
          applyInventoryItem(inventoryPicker.runIdx, inventoryPicker.matIdx, item);
        }}
      />
    </main>
  );
}

export default ProductionDailyReportPanel;
